import { type ReactNode, useMemo } from 'react';
import { HStack, IconUser, Text } from '@direct-frontend/components';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Проблема в текущем файле, тк OrderForm должен быть в widgets
import { ClientPicker, type ClientPickerValue } from '@/features/ClientPicker';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Проблема в текущем файле, тк OrderForm должен быть в widgets
import { LoginButton } from '@/features/LoginButton';
import { type OrderDto } from '@/shared/api/order';
import { Trigger } from '@/shared/components/Trigger';
import { getRequestContext } from '@/shared/lib/requestContext';
import { OzonRegistrationButton } from '../OzonRegistrationButton';
import { PhoneVerificationButton } from '../PhoneVerificationButton';
import styles from './OrderClientInput.module.css';

type OrderClientInputProps = {
    onChange?: (value: ClientPickerValue) => void;
    openRegistrationOzon?: VoidFunction;
    isInvalidOzonUser?: boolean;
    orderClientContext?: OrderDto['client'];
    validationState?: 'valid' | 'invalid';
    value?: ClientPickerValue;
};

const getClientPhone = (client?: ClientPickerValue): string | null => {
    if (!client?.phone) {
        return null;
    }

    return client.phone.replace(/(\+\d)(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 ($2) $3-$4-$5');
};

const getClientDisplayName = (client?: ClientPickerValue): string =>
    [client?.lastName, client?.firstName].filter(Boolean).join(' ');

export const OrderClientInput = (props: OrderClientInputProps) => {
    const { openRegistrationOzon, onChange, orderClientContext, validationState, value, isInvalidOzonUser } = props;
    const phone = getClientPhone(value);
    const fullName = getClientDisplayName(value);
    const { need_phone_verification: needPhoneVerification } = orderClientContext ?? {};
    const { isDigitalOrder } = getRequestContext();

    const ozonRegistrationButton = useMemo<ReactNode>(
        () => openRegistrationOzon && isInvalidOzonUser && <OzonRegistrationButton onClick={openRegistrationOzon} />,
        [isInvalidOzonUser, openRegistrationOzon]
    );

    let phoneWarning: string | undefined;

    if (phone) {
        if (isInvalidOzonUser) {
            phoneWarning = 'К номеру не привязан Ozon ID';
        } else if (needPhoneVerification) {
            phoneWarning = 'Номер не верифицирован';
        }
    }

    return (
        <ClientPicker
            addonTop={<LoginButton className={styles.login} />}
            value={value}
            onChange={onChange}
            needPhoneVerification={Boolean(needPhoneVerification)}
            ozonRegistrationButton={ozonRegistrationButton}
        >
            {({ onOpenModal }) => {
                let rightContent: ReactNode;

                if (phone && needPhoneVerification) {
                    rightContent = <PhoneVerificationButton onClick={() => onOpenModal('phone-verification')} />;
                } else {
                    rightContent = ozonRegistrationButton;
                }

                return (
                    <Trigger
                        caption={phoneWarning}
                        iconLeft={<IconUser size="24" />}
                        onClick={() => onOpenModal('form')}
                        placeholder={isDigitalOrder ? 'Отправитель' : 'Получатель'}
                        rightContent={rightContent}
                        size="m"
                        validationState={validationState}
                    >
                        <HStack as="span" alignItems="baseline">
                            {fullName && <Text size="mc">{fullName},&nbsp;</Text>}
                            {phone && (
                                <Text
                                    color={phoneWarning ? 'red' : 'gray'}
                                    size="mc"
                                    role={phoneWarning ? 'main' : 'supplementary'}
                                >
                                    {phone}
                                </Text>
                            )}
                        </HStack>
                    </Trigger>
                );
            }}
        </ClientPicker>
    );
};
